import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { isApiConfigured } from "../auth-config";
import { postStudentCohortJoin } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { applyJoinSessionResponse } from "../auth/join-session";
import { clearPostLoginReturnTo } from "../auth/post-login-redirect";
import { clearSignupIntent } from "../auth/signup-intent";
import { useStudentCohorts } from "../student/StudentCohortContext";

export default function StudentJoinPage() {
  const { getBearerToken, setEnrollmentSummary, refresh } = useAuth();
  const { reload: reloadCohorts } = useStudentCohorts();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bearer = getBearerToken();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fromUrl = searchParams.get("code")?.trim();
    if (fromUrl) setCode(fromUrl);
  }, [searchParams]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!bearer || !code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await postStudentCohortJoin(bearer, { joinCode: code.trim() });
      applyJoinSessionResponse(result);
      clearSignupIntent();
      clearPostLoginReturnTo();
      refresh();
      if (isApiConfigured()) {
        await setEnrollmentSummary("active");
        await reloadCohorts();
      }
      navigate("/lessons", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not join cohort");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page page-align-start student-page-inner">
      <div className="card card-wide student-page-content">
        <header className="row-header">
          <div>
            <p className="eyebrow">Student</p>
            <h1>Join a cohort</h1>
            <p className="lede muted">Enter the join code your teacher shared with you.</p>
          </div>
        </header>

        {error ? (
          <div className="warn-banner" role="alert">
            {error}
          </div>
        ) : null}

        <form className="stack-form" onSubmit={(ev) => void onSubmit(ev)}>
          <label className="field">
            <span className="field-label">Join code</span>
            <input
              className="input"
              value={code}
              onChange={(ev) => setCode(ev.target.value)}
              placeholder="e.g. 4KJH7Q8WZB"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={!bearer || busy}
              required
            />
          </label>
          <div className="actions">
            <button type="submit" className="btn primary" disabled={!bearer || busy}>
              {busy ? "Joining…" : "Join"}
            </button>
            <Link to="/lessons" className="btn secondary">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}
